import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Ul } from '@vp/swan'
import { DesktopNavigationItem } from './DesktopNavigationItem'
import { useHoverIntent } from '../../../../hooks/useHoverIntent'
import type { NavigationCategory } from '../../../../types/navigation'
import { useI18n } from '../../../../context/TranslationContext'

export const DesktopNavigation: React.FC<Readonly<{ navigationData: NavigationCategory[] }>> = ({ navigationData }) => {
  const { translate } = useI18n()
  const [activeTabId, setActiveTabId] = useState<string | undefined>()
  const activeTabIdRef = useRef(activeTabId)
  activeTabIdRef.current = activeTabId

  const closeFlyout = useCallback(() => setActiveTabId(undefined), [])
  const updateActiveTab = useCallback((id: string) => setActiveTabId(id), [])

  // Close flyout on Escape key press anywhere on the page
  // Uses a ref to avoid re-registering the listener on every activeTab change
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && activeTabIdRef.current) {
        setActiveTabId(undefined)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [])

  const navRef = useHoverIntent<HTMLElement>({ hoverOut: closeFlyout })

  if (!navigationData?.length) return null

  return (
    <Box as="nav" ref={navRef} aria-label={translate('mainNavigation')} px={0}>
      <Ul role="menubar" skin="minimal" style={{ flexDirection: 'row', gap: 0 }}>
        {navigationData.map((item: NavigationCategory) => (
          <DesktopNavigationItem key={item.id} item={item} isActive={activeTabId === item.id} onClose={closeFlyout} updateActiveTab={updateActiveTab} />
        ))}
      </Ul>
    </Box>
  )
}
